import React from 'react';
import { Grid, Button, Chip, Typography, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useCanOpen } from '../../context/PageAccessContext';
import {
  DashboardHeader, StatTile, KpiRow, Section, RowList, AttentionList, StatusChip, BarList, fmtDateTime, fmtDate, LinkButton,
} from './DashboardParts';

export default function VendorDashboard({ data }) {
  const navigate = useNavigate();
  const canOpen = useCanOpen();
  const k = data.kpis;
  const business = data.services.length ? null : 'Choose the services you offer to get started';

  return (
    <>
      <DashboardHeader
        title="Vendor dashboard"
        subtitle={business || 'Your services, offerings and contract steps'}
        chip={canOpen('/dashboard/my-services') ? <Button variant="contained" onClick={() => navigate('/dashboard/my-services')}>My Services</Button> : null}
      />
      <KpiRow>
        <StatTile hero label="Services live" value={k.liveServices} sub={k.pendingServices ? `${k.pendingServices} awaiting contract` : 'visible to families'} to="/dashboard/my-services" />
        <StatTile label="Offerings published" value={`${k.publishedOfferings} / ${k.offerings}`} to="/dashboard/my-services" />
        <StatTile label="Packages listed" value={k.packages} sub={`${k.items} items in total`} to="/dashboard/my-services" />
        <StatTile label="Bookings" value={k.upcomingBookings} sub={k.bookingRequests ? `${k.bookingRequests} new request(s) to answer` : 'confirmed and upcoming'} to="/dashboard/vendor-bookings" />
      </KpiRow>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={6}>
          <Section title="Needs your attention">
            <AttentionList items={data.attention} emptyText="All your services are live and complete." />
          </Section>
        </Grid>
        <Grid item xs={12} lg={6}>
          <Section title="Items listed per service">
            <BarList
              data={data.services.map(s => ({ label: s.label, value: s.items }))}
              emptyText="No services yet."
            />
          </Section>
        </Grid>
        <Grid item xs={12}>
          <Section title="Upcoming bookings" action={<LinkButton to="/dashboard/vendor-bookings">All bookings</LinkButton>}>
            <RowList
              rows={data.upcomingBookings.map(b => ({
                primary: `${b.family_name} · ${b.offering_title}${b.package_name ? ` – ${b.package_name}` : ''}`,
                secondary: `${fmtDate(b.required_date)}${b.required_time ? `, ${b.required_time}` : ''}${b.quantity ? ` · quantity ${b.quantity}` : ''}`,
                right: <Chip size="small" color={b.days_until <= 7 ? 'warning' : 'default'} label={b.days_until === 0 ? 'Today' : `In ${b.days_until} day(s)`} />,
              }))}
              empty="No confirmed bookings yet."
            />
          </Section>
        </Grid>
        <Grid item xs={12}>
          <Section title="Your services" action={<LinkButton to="/dashboard/my-services">Manage</LinkButton>}>
            <RowList
              rows={data.services.map(s => ({
                primary: s.label,
                secondary: `${s.ref || 'No contract'} · ${s.offerings} offering(s) · ${s.packages} package(s) · ${s.items} item(s)`,
                right: <Chip size="small" color={s.live ? 'success' : 'warning'} label={s.live ? 'Live' : 'Not live yet'} />,
              }))}
              empty="You have no contracted services yet. Use Choose Services on My Services to request a contract."
            />
          </Section>
        </Grid>
        <Grid item xs={12} md={6}>
          <Section title="Contracts" action={<LinkButton to="/dashboard/contracts">Open</LinkButton>}>
            <RowList rows={data.contracts.map(c => ({ primary: c.ref, secondary: c.services, right: <StatusChip status={c.status} /> }))} empty="No contracts yet." />
          </Section>
        </Grid>
        <Grid item xs={12} md={6}>
          <Section title="Latest notifications" action={<LinkButton to="/dashboard/notifications">All</LinkButton>}>
            <RowList
              rows={data.notifications.map(n => ({
                primary: n.title,
                secondary: <Stack component="span"><Typography component="span" variant="caption" color="text.secondary">{n.message}</Typography><Typography component="span" variant="caption" color="text.secondary">{fmtDateTime(n.created_at)}</Typography></Stack>,
              }))}
              empty="No new notifications."
            />
          </Section>
        </Grid>
      </Grid>
    </>
  );
}
